/**
 * Venda na Rota no ERP Web (`/rota`). Desde a A0 a tela é o pacote compartilhado
 * `@cafegestor/rota` (o mesmo do CaféGestor Vendedor); aqui o Web a liga à SUA sessão
 * (login do Supabase no navegador), às permissões da empresa ativa (`/me`) e ao SEU cartão de
 * acompanhamento da nota (o mesmo da Venda Direta). Comportamento idêntico ao da R2.
 */
import { RotaHostProvider, RotaPage, type RotaHost } from "@cafegestor/rota";
import { useMemo } from "react";
import { useAuth } from "@/app/auth";
import { useMe } from "@/app/me";
import { Alert } from "@/components/ui";
import { errorMessage } from "@/lib/api";
import { EmissionCard } from "../fiscal/EmissionCard";
import { useEmissionTracking } from "../fiscal/emission-tracking";

/** Acompanha a nota de um pedido (o mesmo cartão da Venda Direta). */
function OrderEmissionTracking({
  documentId,
  since,
}: {
  documentId: string;
  /** desde quando contar o tempo (ISO) */
  since: string;
}) {
  const track = useEmissionTracking({
    documentId,
    finishedAt: since,
    // no celular da rota, nada abre nem baixa sozinho: o vendedor imprime pela impressora
    prefs: { openDanfe: false, download: false },
    pendingTab: null,
  });
  if (track.doc && track.stage) {
    return (
      <EmissionCard
        doc={track.doc}
        stage={track.stage}
        steps={track.steps}
        delivery={track.delivery}
      />
    );
  }
  return track.error ? (
    <Alert tone="warn">
      Não foi possível ler a situação da nota agora: {errorMessage(track.error)}. A emissão continua
      no servidor; a tela tenta de novo sozinha.
    </Alert>
  ) : (
    <p role="status" className="text-sm text-muted-foreground">
      Lendo a situação da nota…
    </p>
  );
}

export function RotaRoute() {
  const { session } = useAuth();
  const me = useMe();
  const userId = session?.user.id ?? null;
  const company = me.data?.activeCompany;
  // as permissões só ESCONDEM botões (como o useCan do Web): quem decide é a API
  const host = useMemo<RotaHost>(() => {
    const perms = new Set(company?.permissions ?? []);
    return {
      userId,
      companyName: company?.name ?? "",
      can: (code) => perms.has(code),
      EmissionTracking: OrderEmissionTracking,
    };
  }, [userId, company]);
  return (
    <RotaHostProvider value={host}>
      <RotaPage />
    </RotaHostProvider>
  );
}
